import {err, ok} from 'framework';
import type {Result} from 'framework';
import {normalizePreprocessOptions} from '../core/preprocess-pipeline';
import {ImagePreprocessError} from '../image-preprocess.error';
import type {PreparedImage, PreprocessImageOptions} from '../types';
import type {ImagePreprocessor} from './image-preprocessor';
import {SerialQueue} from './serial-queue';
import type {WorkerRequest, WorkerResponse} from './worker-protocol';
import {WorkerResponseValidator} from './worker-protocol.validator';

type WorkerOutcome = WorkerResponse | {readonly kind: 'demoted'};

type PendingRequest = {
  readonly resolve: (outcome: WorkerOutcome) => void;
};

const readId = (value: unknown): string | null =>
  typeof value === 'object' && value !== null && 'id' in value && typeof value.id === 'string'
    ? value.id
    : null;

/**
 * Sends each image to the preprocessing Worker and awaits its answer by id.
 * If the Worker cannot be created or crashes, every request from then on runs
 * on the main-thread fallback instead, including the ones in flight.
 */
export class WorkerImagePreprocessor implements ImagePreprocessor {
  private worker: Worker | null = null;
  private demoted = false;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly queue = new SerialQueue();
  private readonly responseValidator = new WorkerResponseValidator();

  constructor(
    private readonly createWorker: () => Worker,
    private readonly fallback: ImagePreprocessor,
    private readonly createId: () => string = () => crypto.randomUUID(),
  ) {}

  preprocess(
    file: File,
    options: PreprocessImageOptions,
  ): Promise<Result<PreparedImage, ImagePreprocessError>> {
    if (this.demoted) {
      return this.fallback.preprocess(file, options);
    }
    const worker = this.ensureWorker();
    if (worker === null) {
      return this.fallback.preprocess(file, options);
    }
    return this.queue.run(() => this.dispatch(worker, file, options));
  }

  private async dispatch(
    worker: Worker,
    file: File,
    options: PreprocessImageOptions,
  ): Promise<Result<PreparedImage, ImagePreprocessError>> {
    const normalized = normalizePreprocessOptions(options);
    const context = {
      acceptedMimeTypes: normalized.constraints.acceptedMimeTypes,
      maxBytes: normalized.constraints.maxBytes,
    };
    const {signal, ...plainOptions} = normalized;
    if (signal?.aborted) {
      return err(new ImagePreprocessError('aborted', context));
    }
    if (this.demoted) {
      return this.fallback.preprocess(file, options);
    }

    const id = this.createId();
    const post = (message: WorkerRequest) => worker.postMessage(message);
    const abort = () => post({id, kind: 'abort'});
    const outcome = await new Promise<WorkerOutcome>(resolve => {
      this.pending.set(id, {resolve});
      signal?.addEventListener('abort', abort, {once: true});
      post({id, kind: 'preprocess', file, options: plainOptions});
    });
    signal?.removeEventListener('abort', abort);
    this.pending.delete(id);

    if (outcome.kind === 'demoted') {
      return this.fallback.preprocess(file, options);
    }
    return outcome.kind === 'ok'
      ? ok(outcome.prepared)
      : err(new ImagePreprocessError(outcome.code, context));
  }

  private ensureWorker(): Worker | null {
    if (this.worker !== null) {
      return this.worker;
    }
    try {
      const worker = this.createWorker();
      worker.addEventListener('message', event => this.onMessage(event));
      worker.addEventListener('error', () => this.demote());
      worker.addEventListener('messageerror', () => this.demote());
      this.worker = worker;
      return worker;
    } catch {
      this.demote();
      return null;
    }
  }

  private onMessage(event: MessageEvent<unknown>): void {
    const parsed = this.responseValidator.validate(event.data);
    if (parsed.ok) {
      this.pending.get(parsed.data.id)?.resolve(parsed.data);
      return;
    }
    const id = readId(event.data);
    if (id !== null) {
      this.pending.get(id)?.resolve({id, kind: 'error', code: 'decode-failed'});
    }
  }

  private demote(): void {
    this.demoted = true;
    this.worker?.terminate();
    this.worker = null;
    for (const request of this.pending.values()) {
      request.resolve({kind: 'demoted'});
    }
    this.pending.clear();
  }
}
